import { AnalysisSummary, TableColumn } from "../../../../../types"
import { ADMIN_PERMISSIONS, chartLabels, TABLE_COLUMN_TYPE } from "../../../../../constants"
import React, { CSSProperties } from "react"
import { KButton, KSpan, KTooltip } from "kahuna-base-react-components"
import { checkAdminPermission, getCurrency } from "../../../../../utility"
//@ts-ignore
import MoneyIcon from "../../../../../assets/admin_icons/money-cash-grey.svg"
//@ts-ignore
import CheckIcon from "../../../../../assets/admin_icons/check-circle.svg"
//@ts-ignore
import CloseIcon from "../../../../../assets/admin_icons/close-circle.svg"

const headerStyle: CSSProperties = { textAlign: "left", width: "25%" }
const cellStyle: CSSProperties = { fontWeight: 400, lineHeight: "20px", color: "#111827" }

const idSelectColumn = (selectedIds: number[], setSelectedIds: (selectedIds: number[]) => void, allIds: number[]) => {
  const column = {
    overrideHeader: () => (
      <div className="flex w-full">
        <input
          type="checkbox"
          checked={allIds.length === selectedIds.length}
          style={{
            accentColor: "#000000"
          }}
          onChange={() => {
            if (selectedIds.length === allIds.length) {
              setSelectedIds([])
            } else {
              setSelectedIds(allIds)
            }
          }}
        />
      </div>
    ),
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: AnalysisSummary) => {
      return (
        <div className="flex w-full" key={`${row?.id}-${selectedIds.includes(Number(row?.id))}`}>
          <input
            checked={selectedIds.includes(Number(row?.id))}
            type="checkbox"
            className="no-parent-trigger"
            style={{
              accentColor: "#000000"
            }}
            onChange={(event) => {
              if (selectedIds.includes(Number(row?.id))) {
                setSelectedIds(selectedIds.filter((id) => id !== Number(row?.id)))
              } else {
                setSelectedIds([...selectedIds, Number(row?.id)])
              }
            }}
          />
        </div>
      )
    },
    headerStyle: { ...headerStyle, width: "3%" },
    cellStyle
  }
  return column
}

const userColumn = (
  isUserBalance: boolean,
  canApprovePayment: boolean,
  onSelectPaymentMethodUserId?: (selectedPaymentMethodUserId?: number, username?: string) => void,
  approvePayment?: (userId?: number, username?: string) => void,
  canUpdateUser?: boolean
) => {
  return {
    header: "User",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: AnalysisSummary) => (
      <div className="flex w-max max-[1200px]:!w-auto">
        <div className="flex flex-row gap-3 items-center">
          <img
            src={row?.photo || "/reports_icons/avatar.svg"}
            alt="profile-photo"
            className="min-w-10 min-h-10 max-h-10 max-w-10 rounded-full"
            width={40}
            height={40}
          />
          <div className="flex flex-col justify-between gap-0.5">
            {row?.userName && <KSpan text={row?.userName} color="#000" fontWeight={500} />}
            <div className="flex">
              <div className="rounded-[4px] bg-[#F7F7F7] p-1 flex justify-center">
                <KSpan
                  text={row?.user}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            </div>
            {isUserBalance && (
              <div className="flex gap-2 mt-0.5">
                <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
                  <KTooltip
                    children={
                      <KSpan
                        text={row.taxTypeName || "-"}
                        color="#111"
                        fontSize={11}
                        fontWeight={500}
                        letterSpacing="0.22px"
                      />
                    }
                    content={<KSpan text="Tax Type" />}
                  />
                </div>

                <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
                  <KTooltip
                    children={
                      <KSpan
                        text={row.userCompany || "-"}
                        color="#111"
                        fontSize={11}
                        fontWeight={500}
                        letterSpacing="0.22px"
                      />
                    }
                    content={<KSpan text="User Company" />}
                  />
                </div>

                {canUpdateUser && (
                  <div
                    className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto"
                    onClick={() => onSelectPaymentMethodUserId && onSelectPaymentMethodUserId(row.userId, row.userName)}
                  >
                    <KTooltip
                      children={
                        <img className="min-w-4 min-h-4" src="/admin_icons/wallet.svg" width={14} height={14} />
                      }
                      content={<KSpan text="Payment Methods" />}
                    />
                  </div>
                )}

                {canApprovePayment && (
                  <div className="flex items-center w-auto">
                    <KTooltip
                      children={
                        <KButton
                          icon={MoneyIcon}
                          height="28px"
                          width="24px"
                          borderRadius={3}
                          padding="1px"
                          background="rgb(242, 254, 103)"
                          onClick={() => approvePayment && approvePayment(row.userId, row.userName)}
                        />
                      }
                      content={<KSpan text="Approve Payment" />}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    headerStyle: { ...headerStyle, width: "10%" },
    cellStyle,
    sortable: true,
    sortFilterField: "user__username"
  }
}

const labelColumn = {
  header: "Label",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const text =
      Object.keys(chartLabels)
        .map((key) => chartLabels[key][row.label])
        .filter((l) => !!l)[0] || row.label
    return (
      <div className="flex">
        <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
          <KSpan text={text} color="#111" fontSize={11} fontWeight={500} lineHeight="12px" letterSpacing="0.22px" />
        </div>
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle
}

const currencyColumn = {
  header: "Currency",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={row.currency}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "currency"
}

const paymentPeriodColumn = (isQuarterly: boolean) => ({
  header: "Payment Period",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const monthlyList = row.monthlyList || []
    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={row.paymentPeriodName || ""}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              return (
                <div key={`payment_period_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={userBalance.date || ""}
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
})

const grossColumn = {
  header: "Gross",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={row.totalGross.toFixed(2)}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "total_gross"
}

const splitGrossColumn = {
  header: "Split Gross",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={`${Number(row.totalAmount).toFixed(2)}`}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "total_amount"
}

const splitNetColumn = {
  header: "Split Net",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={`${Number(row.totalNetAll).toFixed(2)}`}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "total_net"
}

const revenueTotalColumn = (isQuarterly: boolean) => ({
  header: "Revenue Total",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const monthlyList = row.monthlyList || []

    let currency = getCurrency(row.totalNetCurrency)
    if (isQuarterly && monthlyList.length > 0) {
      const currencyList = monthlyList.map((userBalance, index) => {
        return getCurrency(userBalance.totalNetCurrency)
      })
      if ([...new Set(currencyList)].length > 1) currency = ""
    }

    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={`${Number(row.totalNetAll).toFixed(2)} ${currency}`}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              return (
                <div key={`revenue_total_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={`${Number(userBalance.totalNetAll).toFixed(2)} ${getCurrency(userBalance.totalNetCurrency)}`}
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "total_net"
})

const remainingTotalColumn = (isQuarterly: boolean) => ({
  header: "Remaining Total",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const revenueTotal = Number(row.totalNetAll)
    const totalPaid = row.totalApproved

    const monthlyList = row.monthlyList || []

    let currency = getCurrency(row.totalNetCurrency)
    if (isQuarterly && monthlyList.length > 0) {
      const currencyList = monthlyList.map((userBalance, index) => {
        return getCurrency(userBalance.totalNetCurrency)
      })
      if ([...new Set(currencyList)].length > 1) currency = ""
    }

    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={`${(revenueTotal - totalPaid).toFixed(2)} ${currency}`}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              const revenueTotal = Number(userBalance.totalNetAll)
              const totalPaid = userBalance.totalApproved

              return (
                <div key={`remaning_total_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={`${(revenueTotal - totalPaid).toFixed(2)} ${getCurrency(userBalance.totalNetCurrency)}`}
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
})

const royaltyColumn = (isOriginalCurrency: boolean, isQuarterly: boolean) => ({
  header: "Revenue Royalty",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const monthlyList = row.monthlyList || []
    const royalty = Number(row.totalNetAll || 0) - row.affiliateTotal
    let currency = isOriginalCurrency ? getCurrency(row.totalNetCurrency) : ""
    if (isQuarterly && monthlyList.length > 0) {
      const currencyList = monthlyList.map((userBalance, index) => {
        return isOriginalCurrency ? getCurrency(userBalance.totalNetCurrency) : ""
      })
      if ([...new Set(currencyList)].length > 1) currency = ""
    }
    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={`${royalty.toFixed(2)} ${currency}`}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              const royalty = Number(userBalance.totalNetAll || 0) - userBalance.affiliateTotal
              const currency = isOriginalCurrency ? getCurrency(userBalance.totalNetCurrency) : ""
              return (
                <div key={`royalty_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={`${royalty.toFixed(2)} ${currency}`}
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "total_net"
})

const affiliateTotalColumn = (isQuarterly: boolean) => ({
  header: "Revenue Affiliate",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const monthlyList = row.monthlyList || []
    let currency = getCurrency(row.totalNetCurrency)
    if (isQuarterly && monthlyList.length > 0) {
      const currencyList = monthlyList.map((userBalance, index) => {
        return getCurrency(userBalance.totalNetCurrency)
      })
      if ([...new Set(currencyList)].length > 1) currency = ""
    }
    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={`${Number(row.affiliateTotal).toFixed(2)} ${currency}`}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              return (
                <div key={`affiliate_total_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={`${Number(userBalance.affiliateTotal).toFixed(2)} ${getCurrency(
                      userBalance.totalNetCurrency
                    )}`}
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
})

const paidTotalColumn = (isOriginalCurrency: boolean, isQuarterly: boolean) => ({
  header: "Paid Total",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const advanceTotal = row?.totalApprovedDetails?.advance || 0
    const advanceCurrency = isOriginalCurrency && advanceTotal > 0 ? getCurrency(row.advanceCurrency) : ""

    const totalPaid = row?.totalApprovedDetails?.regular || 0
    const affiliatePaid = row.affiliatePaid || 0
    const royaltyPaid = totalPaid - affiliatePaid

    const monthlyList = row.monthlyList || []
    return (
      <div>
        <div className="flex w-full">
          <KTooltip
            backgroundColor="#000"
            arrowColor="#000"
            position="bottom"
            content={
              <div className="w-max flex flex-col gap-1 items-start p-0.5">
                <KSpan
                  text={`Total: ${totalPaid.toFixed(2)} ${isOriginalCurrency ? getCurrency(row.totalNetCurrency) : ""}`}
                  fontSize={12}
                  fontWeight={500}
                  lineHeight="16px"
                  color="#FFF"
                />
                <KSpan
                  text={`Royalty: ${royaltyPaid.toFixed(2)} ${
                    isOriginalCurrency ? getCurrency(row.totalNetCurrency) : ""
                  }`}
                  fontSize={12}
                  fontWeight={500}
                  lineHeight="16px"
                  color="#FFF"
                />
                <KSpan
                  text={`Affiliate: ${affiliatePaid.toFixed(2)} ${
                    isOriginalCurrency ? getCurrency(row.totalNetCurrency) : ""
                  }`}
                  fontSize={12}
                  fontWeight={500}
                  lineHeight="16px"
                  color="#FFF"
                />
                <div className="mt-2" />
                <KSpan
                  text={`Advance Deduction: ${advanceTotal ? advanceTotal.toFixed(2) : "-"} ${advanceCurrency}`}
                  fontSize={12}
                  fontWeight={500}
                  lineHeight="16px"
                  color="#FFF"
                />
              </div>
            }
          >
            <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
              <KSpan
                text={`${Number(row.totalApproved).toFixed(2)} ${
                  isOriginalCurrency ? getCurrency(row.totalNetCurrency) : ""
                }`}
                color="#111"
                fontSize={11}
                fontWeight={500}
                lineHeight="12px"
                letterSpacing="0.22px"
              />
            </div>
          </KTooltip>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              const advanceTotal = userBalance?.totalApprovedDetails?.advance || 0
              const advanceCurrency =
                isOriginalCurrency && advanceTotal > 0 ? getCurrency(userBalance.advanceCurrency) : ""

              const totalPaid = userBalance?.totalApprovedDetails?.regular || 0
              const affiliatePaid = userBalance.affiliatePaid || 0
              const royaltyPaid = totalPaid - affiliatePaid

              return (
                <div key={`paid_total_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KTooltip
                    backgroundColor="#000"
                    arrowColor="#000"
                    position="bottom"
                    content={
                      <div className="w-max flex flex-col gap-1 items-start p-0.5">
                        <KSpan
                          text={`Total: ${totalPaid.toFixed(2)} ${
                            isOriginalCurrency ? getCurrency(userBalance.totalNetCurrency) : ""
                          }`}
                          fontSize={12}
                          fontWeight={500}
                          lineHeight="16px"
                          color="#FFF"
                        />
                        <KSpan
                          text={`Royalty: ${royaltyPaid.toFixed(2)} ${
                            isOriginalCurrency ? getCurrency(userBalance.totalNetCurrency) : ""
                          }`}
                          fontSize={12}
                          fontWeight={500}
                          lineHeight="16px"
                          color="#FFF"
                        />
                        <KSpan
                          text={`Affiliate: ${affiliatePaid.toFixed(2)} ${
                            isOriginalCurrency ? getCurrency(userBalance.totalNetCurrency) : ""
                          }`}
                          fontSize={12}
                          fontWeight={500}
                          lineHeight="16px"
                          color="#FFF"
                        />
                        <div className="mt-2" />
                        <KSpan
                          text={`Advance Deduction: ${advanceTotal ? advanceTotal.toFixed(2) : "-"} ${advanceCurrency}`}
                          fontSize={12}
                          fontWeight={500}
                          lineHeight="16px"
                          color="#FFF"
                        />
                      </div>
                    }
                  >
                    <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
                      <KSpan
                        text={`${Number(userBalance.totalApproved).toFixed(2)} ${
                          isOriginalCurrency ? getCurrency(userBalance.totalNetCurrency) : ""
                        }`}
                        color="#111"
                        fontSize={11}
                        fontWeight={500}
                        lineHeight="12px"
                        letterSpacing="0.22px"
                      />
                    </div>
                  </KTooltip>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
})

const kahunaShareColumn = {
  header: "Kahuna Share",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={`${(Number(row.totalGross) - Number(row.totalNet)).toFixed(2)}`}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
}

const advanceTotalColumn = (isOriginalCurrency: boolean, isQuarterly: boolean) => ({
  header: "Advance Total",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const monthlyList = row.monthlyList || []
    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={
                row.advanceTotal > 0
                  ? `${Number(row.advanceTotal).toFixed(2)} ${
                      isOriginalCurrency ? getCurrency(row.advanceCurrency) : ""
                    }`
                  : "-"
              }
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              return (
                <div key={`advance_total_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={
                      userBalance.advanceTotal > 0
                        ? `${Number(userBalance.advanceTotal).toFixed(2)} ${
                            isOriginalCurrency ? getCurrency(userBalance.advanceCurrency) : ""
                          }`
                        : "-"
                    }
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
})

const remainingAdvanceTotalColumn = (isOriginalCurrency: boolean, isQuarterly: boolean) => ({
  header: "Remaining Advance",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const remaningTotal =
      Number(row.advanceTotal) - Number(row.paidTotal) > 0 ? Number(row.advanceTotal) - Number(row.paidTotal) : 0

    const monthlyList = row.monthlyList || []
    return (
      <div>
        <div className="flex w-full">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
            <KSpan
              text={
                remaningTotal > 0
                  ? `${remaningTotal.toFixed(2)} ${isOriginalCurrency ? getCurrency(row.advanceCurrency) : ""}`
                  : "-"
              }
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
        {isQuarterly && monthlyList.length > 0 && (
          <div className="items-center p-1 w-auto mt-3 mb-3">
            {monthlyList.map((userBalance, index) => {
              const remaningTotal =
                Number(userBalance.advanceTotal) - Number(userBalance.paidTotal) > 0
                  ? Number(userBalance.advanceTotal) - Number(userBalance.paidTotal)
                  : 0

              return (
                <div key={`remaning_advance_total_${index}`} className="mt-1" style={{ borderTop: "1px dashed grey" }}>
                  <KSpan
                    text={
                      remaningTotal > 0
                        ? `${remaningTotal.toFixed(2)} ${
                            isOriginalCurrency ? getCurrency(userBalance.advanceCurrency) : ""
                          }`
                        : "-"
                    }
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
})

const shareColumn = {
  header: "Share",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={`${Number((row.totalAmount / row.totalGross) * 100).toFixed(2)}`}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: false
}

const approvedColumn = {
  header: "Reports Approved",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full gap-2">
      <img className="min-w-5 max-w-5 min-h-5 max-h-5" src={row.approved ? CheckIcon : CloseIcon} alt="status-icon" />
      {row.approved && row.approvedDate && <KSpan fontSize={12} text={`(${row.approvedDate})`} />}
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "approved"
}

const paymentApprovedColumn = {
  header: "Payment Approved",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full gap-2">
      <img
        className="min-w-5 max-w-5 min-h-5 max-h-5"
        src={row.paymentApproved ? CheckIcon : CloseIcon}
        alt="status-icon"
      />
      {row.paymentApproved && row.paymentDate && <KSpan fontSize={12} text={`(${row.paymentDate})`} />}
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "payment_approved"
}

const transferDateColumn = {
  header: "Transfer Date",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={row.transferDate || ""}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "5%" },
  cellStyle,
  sortable: true,
  sortFilterField: "transfer_date"
}

const platformExist = {
  header: "Platform Exist",
  type: TABLE_COLUMN_TYPE.BOOL,
  attribute: "platformExist",
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "platformExist"
}

const approvedTotalNetColumn = {
  header: "Approved Total Net",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => (
    <div className="flex w-full">
      <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
        <KSpan
          text={Number(row.totalNet).toFixed(2)}
          color="#111"
          fontSize={11}
          fontWeight={500}
          lineHeight="12px"
          letterSpacing="0.22px"
        />
      </div>
    </div>
  ),
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle,
  sortable: true,
  sortFilterField: "total_net"
}

const approvePercentage = {
  header: "Approval Rate",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const approveCount = row.approveCount || 0
    const totalCount = row.totalCount || 0
    const percentage = (totalCount > 0 ? (approveCount / totalCount) * 100 : 0).toFixed(2)
    return (
      <div className="flex w-full">
        <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
          <KSpan
            text={`${approveCount} / ${totalCount} (${percentage}%)`}
            color="#111"
            fontSize={11}
            fontWeight={500}
          />
        </div>
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "8%" },
  cellStyle
}

const threshHoldColumn = () => ({
  header: "Revenue Approved / Threshold",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const revenueTotalAllApproved = row.revenueTotalAllApproved || 0
    const paymentThreshHold = row.paymentThreshold || 0
    const percentage = (paymentThreshHold > 0 ? (revenueTotalAllApproved / paymentThreshHold) * 100 : 0).toFixed(2)
    const revenueApprovedDateList = row.revenueApprovedDateList || []

    const color = revenueTotalAllApproved >= paymentThreshHold ? "#56E099" : "#FF5865"
    return (
      <div className="flex w-full">
        <KTooltip
          width="200px"
          children={
            <div className="rounded-[4px] flex items-center p-1 w-auto">
              <KSpan
                text={`${revenueTotalAllApproved.toFixed(1)} / ${paymentThreshHold.toFixed(
                  1
                )} (${percentage}%) ${getCurrency(row.totalNetCurrency)}`}
                color={color}
                fontSize={11}
                fontWeight={500}
              />
            </div>
          }
          content={
            <div>
              <KSpan text={`Date List: ${revenueApprovedDateList.join(", ")}`} />
            </div>
          }
          position="left"
        />
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "10%" },
  cellStyle
})

const paymentApprovePercentage = {
  header: "Payment Approval Rate",
  type: TABLE_COLUMN_TYPE.FUNCTION,
  render: (row: AnalysisSummary) => {
    const paymentApproveCount = row.paymentApproveCount || 0
    const totalCount = row.totalCount || 0
    const percentage = (totalCount > 0 ? (paymentApproveCount / totalCount) * 100 : 0).toFixed(2)
    return (
      <div className="flex w-full">
        <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto">
          <KSpan
            text={`${paymentApproveCount} / ${totalCount} (${percentage}%)`}
            color="#111"
            fontSize={11}
            fontWeight={500}
          />
        </div>
      </div>
    )
  },
  headerStyle: { ...headerStyle, width: "8%" },
  cellStyle
}

export const reportsApprovalPlatformColumns = (
  selectedIds: number[],
  setSelectedIds: (selectedIds: number[]) => void,
  allIds: number[],
  showIdSelect: boolean
): TableColumn[] => {
  const columns: TableColumn[] = [
    userColumn(false, false),
    labelColumn,
    currencyColumn,
    splitGrossColumn,
    splitNetColumn,
    approvedColumn,
    paymentApprovedColumn,
    transferDateColumn
  ]
  if (showIdSelect) {
    columns.unshift(idSelectColumn(selectedIds, setSelectedIds, allIds))
  }
  return columns
}

export const reportsApprovalTrackColumns = (): TableColumn[] => {
  return [
    userColumn(false, false),
    labelColumn,
    currencyColumn,
    grossColumn,
    splitGrossColumn,
    splitNetColumn,
    shareColumn
  ]
}

export const reportsApprovalStoreFrontColumns = (): TableColumn[] => {
  return [
    userColumn(false, false),
    { ...labelColumn, header: "Country" },
    currencyColumn,
    splitGrossColumn,
    splitNetColumn
  ]
}

export const reportsApprovalAllPlatformTotalColumns = (): TableColumn[] => {
  return [
    labelColumn,
    splitGrossColumn,
    splitNetColumn,
    currencyColumn,
    platformExist,
    approvePercentage,
    paymentApprovePercentage
  ]
}

export const reportsApprovalAllAccountColumns = (): TableColumn[] => {
  const columns: TableColumn[] = [
    userColumn(false, false),
    grossColumn,
    splitGrossColumn,
    kahunaShareColumn,
    splitNetColumn,
    paidTotalColumn(false, false),
    currencyColumn,
    advanceTotalColumn(false, false),
    remainingAdvanceTotalColumn(false, false)
  ]
  return columns
}

export const reportsUserBalanceColumns = (
  isQuarterly: boolean,
  onSelectPaymentMethodUserId?: (selectedPaymentMethodUserId?: number, username?: string) => void,
  approvePayment?: (userId?: number, username?: string) => void,
  userAdminPermissions?: string
): TableColumn[] => {
  const columns: TableColumn[] = [
    userColumn(
      true,
      checkAdminPermission(ADMIN_PERMISSIONS.REPORTS_APPROVAL_PAYMENT_APPROVE, userAdminPermissions),
      onSelectPaymentMethodUserId,
      approvePayment,
      checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, userAdminPermissions)
    ),
    paymentPeriodColumn(isQuarterly),
    royaltyColumn(true, isQuarterly),
    affiliateTotalColumn(isQuarterly),
    revenueTotalColumn(isQuarterly),
    paidTotalColumn(true, isQuarterly),
    remainingTotalColumn(isQuarterly),
    advanceTotalColumn(true, isQuarterly),
    remainingAdvanceTotalColumn(true, isQuarterly),
    threshHoldColumn()
  ]
  return columns
}
